import { graphql, useLazyLoadQuery } from 'react-relay';

import type { useRovoActivationIdQuery } from './__generated__/useRovoActivationIdQuery.graphql';
import type { AgentSessionsScope } from './types';

type RovoActivationResolution =
	| Readonly<{ status: 'ready'; rovoActivationId: string }>
	| Readonly<{
			status: 'unavailable';
			reason: 'missing-identity' | 'no-active-rovo-activation';
	  }>;

const getNonEmptyString = (value: unknown): string | undefined => {
	return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

export const useRovoActivationId = ({
	accountId,
	cloudId,
	rovoActivationId,
}: AgentSessionsScope): RovoActivationResolution => {
	const validAccountId = getNonEmptyString(accountId);
	const suppliedActivationId = getNonEmptyString(rovoActivationId);
	const validCloudId = getNonEmptyString(cloudId);
	const shouldResolveActivation = validAccountId != null && validCloudId != null;

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks -- Workspace discovery is an internal part of the public sessions hook.
	const activationData = useLazyLoadQuery<useRovoActivationIdQuery>(
		graphql`
			query useRovoActivationIdQuery($cloudId: ID!, $shouldResolveActivation: Boolean!) {
				tenantContexts(cloudIds: [$cloudId]) @include(if: $shouldResolveActivation) {
					activationIdByProduct(product: "rovo") {
						active
					}
				}
			}
		`,
		{
			cloudId: validCloudId ?? '',
			shouldResolveActivation,
		},
		{ fetchPolicy: 'store-or-network' },
	);

	if (validAccountId == null || (suppliedActivationId == null && validCloudId == null)) {
		return { status: 'unavailable', reason: 'missing-identity' };
	}

	if (validCloudId == null && suppliedActivationId != null) {
		return { status: 'ready', rovoActivationId: suppliedActivationId };
	}

	const resolvedActivationId = getNonEmptyString(
		activationData.tenantContexts?.[0]?.activationIdByProduct?.active,
	);

	return resolvedActivationId == null
		? { status: 'unavailable', reason: 'no-active-rovo-activation' }
		: { status: 'ready', rovoActivationId: resolvedActivationId };
};
